import { ReportContext } from "@reporting/rp-types/analytics";
import { ExportType } from "../types";
import { BridgeMessage, ClientMessages, HostMessages, isBridgeMessage, RPEX_PROTOCOL } from "./protocol";

let uidCounter = 0;

/**
 * The embedding side of the bridge. Owned by the sandbox host component; talks to
 * the extension page in its iframe. Every 'ready' (first load and every reload
 * after re-parenting) is answered with the last known init state, so the page
 * always recovers on its own.
 */
export class ExtensionBridge {

	readonly uid = `rpexb-${++uidCounter}`;

	private _iframe: HTMLIFrameElement;
	private _pendingInit: HostMessages['init'] = {};
	private _cache = new Map<string, unknown>();

	onReady: () => void;
	onLoad: (payload: ClientMessages['load']) => void;
	onDetail: (payload: ClientMessages['detail']) => void;
	onBroadcast: (payload: ClientMessages['broadcast']) => void;
	onError: (payload: ClientMessages['error']) => void;

	private _attached = false;

	attach(iframe: HTMLIFrameElement) {
		this._iframe = iframe;
		if (this._attached) return;
		this._attached = true;
		window.addEventListener('message', this._onMessage);
	}

	dispose() {
		window.removeEventListener('message', this._onMessage);
		this._attached = false;
		this._iframe = null;
	}

	private _onMessage = (e: MessageEvent) => {
		if (!this._iframe || e.source !== this._iframe.contentWindow) return;
		if (!isBridgeMessage<ClientMessages>(e.data)) return;
		const message = e.data as BridgeMessage<ClientMessages>;

		switch (message.type) {
			case 'ready':
				this.send('init', { ...this._pendingInit, cache: [...this._cache] });
				this.onReady?.();
				break;
			case 'cache': {
				const { hash, value } = message.payload as ClientMessages['cache'];
				this._cache.set(hash, value);
				break;
			}
			case 'load':
				this.onLoad?.(message.payload as ClientMessages['load']);
				break;
			case 'detail':
				this.onDetail?.(message.payload as ClientMessages['detail']);
				break;
			case 'broadcast':
				this.onBroadcast?.(message.payload as ClientMessages['broadcast']);
				break;
			case 'error':
				this.onError?.(message.payload as ClientMessages['error']);
				break;
		}
	};

	send<TType extends keyof HostMessages>(type: TType, payload: HostMessages[TType]) {
		this._iframe?.contentWindow?.postMessage({ rpex: RPEX_PROTOCOL, type, payload }, window.location.origin);
	}

	/** remember the context for (re)inits and push it to a running page */
	setContext(context: ReportContext) {
		// structured clone boundary: strip anything non-serializable up front
		this._pendingInit.context = context ? JSON.parse(JSON.stringify(context)) : context;
		this.send('context', { context: this._pendingInit.context });
	}

	/** remember edited main resource content so the next (re)init delivers it */
	setMainResourceContent(content: string) {
		this._pendingInit.mainResourceContent = content;
	}

	/** drop the mirrored query results - an explicit reload wants fresh data */
	clearCache() {
		this._cache.clear();
	}

	refresh(mainResourceContent?: string) {
		if (mainResourceContent != null) this._pendingInit.mainResourceContent = mainResourceContent;
		this.send('refresh', { mainResourceContent });
	}

	action(id: string, data?: unknown) {
		this.send('action', { id, data });
	}

	export(type: ExportType) {
		this.send('export', { type });
	}

}
